import _extends from "@babel/runtime/helpers/extends";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Avatar, Button, Card, Dropdown, Grid, Icon, Link, Picture, Spinner, Text } from "~/components-shared";
import { AuthoritiesEnum, CRUDActionEnum, IntegrationTypeEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import { useHistory } from "react-router-dom";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import styled, { useTheme } from "styled-components";
import lists from "~/lib/lists";
import useAbacExt from "~/hooks/abac";
import topicActionCreators from "~/store/topics/actions";
import { TopicActionEnum } from "~/store/topics/types";
import { useDispatch, useSelector } from "react-redux";
import topicSelectors from "~/store/topics/selectors";
import toastsActionCreators from "~/store/toasts/actions";
import integrationSelectors from "~/store/integrations/selectors";
import authSelectors from "~/store/auth/selectors";
const StyledGrid = styled(Grid)`
  border-radius: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.shape.borderRadius.primary;
}};
`;
const StyledAvatar = styled(Avatar)`
  box-shadow: 0 0 0 2px ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.background.primary;
}};
`;
const Members = styled.div`
  display: flex;
  flex-wrap: wrap;
`;
const Sources = styled.div`
  display: inline-flex;
  vertical-align: sub;
  flex-wrap: wrap;
  gap: 0.5rem;
`;
const TopicInfoCard = _ref3 => {
  let {
    topic,
    ...rest
  } = _ref3;
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const history = useHistory();
  const {
    userHasOneOf
  } = useAbacExt();
  const {
    startDate
  } = topic;
  const plannedStartDate = startDate ? moment(startDate) : undefined;
  const dispatch = useDispatch();
  const [hoverFollow, setHoverFollow] = useState(false);
  const followStatus = useSelector(topicSelectors.getStatusByFollow(topic.id));
  const followError = useSelector(topicSelectors.getErrorByFollow(topic.id));
  const sendStatus = useSelector(topicSelectors.getStatusByTopicSend(topic.id));
  const sendError = useSelector(topicSelectors.getErrorByTopicSend(topic.id));
  const error = followError || sendError;
  const user = useSelector(authSelectors.getUser);
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const handleMemberClick = id => {
    history.push(user.id === id ? RoutesEnum.PROFILE : RoutesEnum.MEMBER.replace(":memberId", id));
  };
  const handleEdit = () => {
    dispatch(topicActionCreators.actionChanged(CRUDActionEnum.UPDATE, topic.id));
    mixpanel.track(`Topic – Edit "${topic.name}"`);
  };
  const handleRetro = () => {
    dispatch(topicActionCreators.actionChanged(TopicActionEnum.RUN_RETRO, topic.id));
    mixpanel.track(`Topic – Run Retro "${topic.name}"`);
  };
  const handleFollow = callback => {
    if (topic.isFollowed) {
      dispatch(topicActionCreators.unfollowStarted(topic.id, _ref4 => {
        let {
          data
        } = _ref4;
        return data && typeof callback === "function" && callback();
      }));
      mixpanel.track(`Topic – Unfollow "${topic.name}"`);
    } else {
      dispatch(topicActionCreators.followStarted(topic.id, _ref5 => {
        let {
          data
        } = _ref5;
        return data && typeof callback === "function" && callback();
      }));
      mixpanel.track(`Topic – Follow "${topic.name}"`);
    }
  };
  const handleShareInSlack = () => {
    dispatch(topicActionCreators.actionChanged(TopicActionEnum.SHARE_IN_SLACK, topic.id));
    mixpanel.track(`Topic – Share in Slack "${topic.name}"`);
  };
  const handleSendInSlack = callback => {
    dispatch(topicActionCreators.sendSlackStarted(topic.id, _ref6 => {
      let {
        data
      } = _ref6;
      if (data === topic.id) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("TopicInfoCard.reportSent"),
          timeout: 10000
        }));
        callback();
      }
    }));
    mixpanel.track(`Topic – Send in Slack "${topic.name}"`);
  };
  const handleDelete = () => {
    dispatch(topicActionCreators.actionChanged(CRUDActionEnum.DELETE, topic.id));
    mixpanel.track(`Topic – Delete "${topic.name}"`);
  };
  return /*#__PURE__*/React.createElement(Card, _extends({
    color: "transparent",
    border: true
  }, rest), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 8,
    gap: 3,
    template: !!slackIntegration || userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) ? "auto auto auto auto auto auto 140px 2.7rem" : "auto auto auto auto auto auto 140px",
    s: {
      px: 4,
      py: 3
    },
    align: "stretch"
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2
    }
  }, t("TopicInfoCard.uniqueTag")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, topic.trigger && !!topic.sources.length ? `#${topic.trigger}` : t("TopicInfoCard.noTag"), /*#__PURE__*/React.createElement(Icon, {
    s: {
      ml: 1,
      mt: -0.5
    },
    color: "secondary",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": "Unique <strong>hashtag</strong> used to explicitly tag relevant messages, events, tickets, and content. <br/><br/>Use this tag in your <strong>title</strong>, <strong>description</strong>, <strong>message</strong> or <strong>comment</strong> to force-map Footprint under specific Topic.",
    style: {
      cursor: "help"
    },
    src: "infoOutline"
  }))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2
    }
  }, t("TopicInfoCard.startDate")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, plannedStartDate ? plannedStartDate.format("ddd, MMM D") : "N/A")), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 1.5
    }
  }, t("TopicInfoCard.owner")), /*#__PURE__*/React.createElement(Link, {
    onClick: () => handleMemberClick(topic.owner.id),
    style: {
      verticalAlign: "text-bottom"
    }
  }, /*#__PURE__*/React.createElement(Avatar, {
    size: 7,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": `${topic.owner.firstName} ${topic.owner.lastName} (${topic.owner.email})`,
    style: {
      verticalAlign: "text-bottom"
    },
    user: topic.owner
  }))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 1.5
    }
  }, t("TopicInfoCard.stakeholders")), topic.members.length ? /*#__PURE__*/React.createElement(Members, null, topic.members.map(member => /*#__PURE__*/React.createElement(Link, {
    key: member.id,
    style: {
      verticalAlign: "text-bottom"
    },
    onClick: () => handleMemberClick(member.id),
    s: {
      mr: -1,
      mb: -1
    }
  }, /*#__PURE__*/React.createElement(StyledAvatar, {
    size: 7,
    user: member,
    style: {
      verticalAlign: "text-bottom"
    },
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": `${member.firstName} ${member.lastName} (${member.email})`
  })))) : /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    weight: "bold",
    display: "block",
    s: {
      pt: 0.5
    }
  }, t("common.actions.no"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2
    }
  }, t("TopicInfoCard.sources")), topic.sources.length ? /*#__PURE__*/React.createElement(Sources, null, topic.sources.map(source => /*#__PURE__*/React.createElement(Picture, {
    key: source,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": lists.common.apps.names[source],
    width: "1.5rem",
    height: "1.5rem",
    border: "squared",
    backgroundSize: "contain",
    src: lists.common.apps.logos(theme)[source]
  }))) : /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    weight: "bold"
  }, t("common.actions.no"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2
    }
  }, t("TopicInfoCard.visibility")), /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    weight: "bold"
  }, lists.topics.visibility.names[topic.visibility])), /*#__PURE__*/React.createElement(Grid.Cell, {
    align: "center",
    justify: "end"
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 1.5
    }
  }, t("TopicInfoCard.receiveInSlack")), /*#__PURE__*/React.createElement(Button, {
    onClick: () => handleFollow(),
    onMouseEnter: () => setHoverFollow(true),
    onMouseLeave: () => setHoverFollow(false),
    display: "inline-block",
    color: topic?.isFollowed ? "translucent" : "primary",
    fontSize: 3,
    loading: followStatus === StatusStateEnum.LOADING,
    s: {
      pl: 0,
      pr: 2,
      py: 1.5
    },
    outline: false,
    textAlign: "center",
    maxWidth: "140px",
    fullWidth: true,
    style: {
      minWidth: "140px"
    }
  }, topic.isFollowed && /*#__PURE__*/React.createElement(Icon, {
    src: "tick",
    s: {
      mr: 2
    }
  }), topic.isFollowed ? hoverFollow ? t("TopicInfoCard.unfollow") : t("TopicInfoCard.following") : t("TopicInfoCard.follow"))), (!!slackIntegration || userHasOneOf([AuthoritiesEnum.TOPICS_WRITE, AuthoritiesEnum.TOPICS_WRITE, AuthoritiesEnum.TOPICS_WRITE])) && /*#__PURE__*/React.createElement(Grid.Cell, {
    align: "center",
    justify: "end"
  }, /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    display: "inline-block",
    handlerProps: {
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      color: theme.colors.text.primary,
      display: "block"
    }),
    minWidth: "180px",
    fullWidth: true
  }, _ref7 => {
    let {
      isOpen,
      onItemClick
    } = _ref7;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleEdit();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "pencil",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")), userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      textWrap: true,
      onClick: () => {
        handleRetro();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "searchHistory",
      color: "primary",
      s: {
        mr: 2
      }
    }), /*#__PURE__*/React.createElement(Text, null, t("TopicInfoCard.runRetro"))), !!slackIntegration && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleSendInSlack(() => {
          onItemClick();
        });
      }
    }, sendStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "send",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("TopicInfoCard.sendToSlack")), !!slackIntegration && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleShareInSlack();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "share",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("TopicInfoCard.shareInSlack")), userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleDelete();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "bin",
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete")));
  }))));
};
export default TopicInfoCard;